export type ModelType = 'OD' | 'VE' | 'PC' | 'OOS';
export type RunType = 'OD' | 'PR' | 'PC' | 'Full' | 'PR_Compliance';
export type RunStatus =
  | 'pending'
  | 'ray_running'
  | 'ray_completed'
  | 'pc_running'
  | 'completed'
  | 'outdated'
  | 'failed'
  | 'cancelled'
  | 'timed_out';
export type DatasetStatus = 'draft' | 'locked';
export type LabelSource = 'human' | 'verified_inference';

export const RUN_TIMEOUT_HOURS = 24;

export const ACTIVE_RUN_STATUSES = new Set<RunStatus>([
  'pending',
  'ray_running',
  'ray_completed',
  'pc_running',
]);
export const RETRYABLE_RUN_STATUSES = new Set<RunStatus>([
  'failed',
  'cancelled',
  'timed_out',
  'outdated',
]);

export const LABEL_SOURCE_LABELS: Record<LabelSource, string> = {
  human: 'Human',
  verified_inference: 'Verified Inference',
};

export function isRunTimedOut(run: EvalRun): boolean {
  if (!ACTIVE_RUN_STATUSES.has(run.status)) return false;
  const anchor = run.status === 'pending' ? run.triggered_at : run.started_at;
  if (!anchor) return false;
  return (Date.now() - new Date(anchor).getTime()) / 36e5 > RUN_TIMEOUT_HOURS;
}

export interface AssignmentChain {
  chain_id: number;
  chain_name: string;
}

export interface EvalModel {
  model_id: string;
  model_name: string;
  model_type: ModelType;
  description: string | null;
  is_archived: boolean;
  is_default: boolean;
  params: Record<string, unknown>;
  registered_at: string;
  updated_at: string | null;
  // chains routed to this model via store-settings overrides (list response only)
  assigned_chains?: AssignmentChain[];
  // detail-only fields
  registered_by?: string | null;
}

export interface EvalGtDataset {
  dataset_id: string;
  dataset_name: string;
  description: string | null;
  status: DatasetStatus;
  gt_sample_ids: string[];
  sample_count: number;
  tags: string[];
  created_by: string | null;
  created_at: string;
  locked_at: string | null;
  locked_by: string | null;
  last_sample_updated_at: string | null;
}

export interface InferenceCorrection {
  corrected_by: string;
  corrected_at: string;
  field: string;
  note: string | null;
}

export interface PogItemSnapshot {
  il_item_id: number;
  mod_coverage_id: number;
  upc: string;
  camera_id: number;
  shelf: number;
  position: number;
  mod: number;
  sequence_number: number;
}

export interface EvalGtSample {
  gt_sample_id: string;
  image_id: number | null;
  image_url: string | null;
  store_id: number | null;
  chain_id: number | null;
  camera_id: number | null;
  captured_at?: string | null;
  ingested_at: string;
  dataset_id: string | null;
  label_source: LabelSource;
  annotated_by: string | null;
  annotated_at: string | null;
  ingested_by: string | null;
  has_pr_inference: boolean;
  has_pc_run_snapshot: boolean;
  // detail-only fields (absent on list responses)
  pr_inference?: Record<string, unknown> | null;
  pc_inference?: Record<string, unknown> | null;
  pog_items?: PogItemSnapshot[] | null;
  corrections?: InferenceCorrection[];
  created_at?: string;
}

export function isSampleReady(s: EvalGtSample): boolean {
  return s.has_pr_inference && s.has_pc_run_snapshot;
}

export interface OdMetrics {
  precision: number | null;
  recall: number | null;
  f1: number | null;
  total_objects: number;
}

export interface OdRunResult {
  cartons: OdMetrics;
  products: OdMetrics;
  shelves: OdMetrics;
  mods: OdMetrics;
  template_boxes: OdMetrics;
  price_tags: OdMetrics;
}

export interface PrMetrics {
  correct_top1_sure_cnt: number;
  correct_top1_unsure_cnt: number;
  incorrect_top1_sure_cnt: number;
  incorrect_top1_unsure_cnt: number;
  correct_top3_cnt: number;
  total_objects: number;
  avg_exp_confidence: number | null;
  avg_top1_confidence: number | null;
  avg_top3_confidence: number | null;
  recommended_similarity_thr: number | null;
  recommended_delta_thr: number | null;
}

export interface PrRunResult {
  cartons: PrMetrics;
  products: PrMetrics;
}

// correct_sure / total — high-confidence correct rate
export function prCorrectSureRate(pr: PrMetrics | undefined): number | null {
  if (!pr || pr.total_objects === 0) return null;
  return pr.correct_top1_sure_cnt / pr.total_objects;
}

// (correct_sure + correct_unsure) / total — overall top1 accuracy; use as single-value summary
export function prTop1Accuracy(pr: PrMetrics | undefined): number | null {
  if (!pr || pr.total_objects === 0) return null;
  return (pr.correct_top1_sure_cnt + pr.correct_top1_unsure_cnt) / pr.total_objects;
}

// incorrect_sure / total — high-confidence wrong rate (lower is better)
export function prIncorrectSureRate(pr: PrMetrics | undefined): number | null {
  if (!pr || pr.total_objects === 0) return null;
  return pr.incorrect_top1_sure_cnt / pr.total_objects;
}

// incorrect_unsure / total — low-confidence wrong rate (lower is better)
export function prIncorrectUnsureRate(pr: PrMetrics | undefined): number | null {
  if (!pr || pr.total_objects === 0) return null;
  return pr.incorrect_top1_unsure_cnt / pr.total_objects;
}

// correct_top3 / total — accuracy within top-3 candidates
export function prTop3Accuracy(pr: PrMetrics | undefined): number | null {
  if (!pr || pr.total_objects === 0) return null;
  return pr.correct_top3_cnt / pr.total_objects;
}

export interface PcClassMetrics {
  precision: number | null;
  recall: number | null;
  f1: number | null;
  accuracy: number | null;
  support: number;
}

export interface PogConfusionMatrix {
  matrix: number[][];
  labels: string[];
}

export interface PcPogMetrics {
  compliant: PcClassMetrics;
  non_compliant: PcClassMetrics;
  out: PcClassMetrics;
  faceover: PcClassMetrics;
  confusion_matrix: PogConfusionMatrix;
}

export interface RogAxisMetrics {
  tp: number;
  fp: number;
  fn: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
  support: number;
}

export interface PcRogMetrics {
  status_by_position: RogAxisMetrics;
  item_number_by_position: RogAxisMetrics;
  item_number_by_image: RogAxisMetrics;
}

export interface PcRunResult {
  pog: PcPogMetrics;
  rog: PcRogMetrics;
}

/**
 * PR Compliance metrics (DLI-1108).
 * Defined here so the canonical Compare page can detect this section
 * without depending on the pr-compliance subtree.
 */
export interface PrCompPrecisionRecall {
  // Backend may emit null when no positives are predicted (precision undefined)
  // or when no positives exist in GT (recall undefined).
  precision: number | null;
  recall: number | null;
  f1?: number | null;
  support?: number;
}

export interface PrCompResult {
  /** Out + faceover folded into one positive class. Primary metric. */
  folded: PrCompPrecisionRecall;
  /** Outs only (empty slot detection). Legacy and PC v2 both compete. */
  out_only: PrCompPrecisionRecall;
  /** Faceovers only (wrong product in slot). Legacy is structurally blind. */
  faceover_only: PrCompPrecisionRecall | null;
  /** Stability across consecutive frames. Only present in sequence_mode runs. */
  stability: number | null;
  /** Histogram of the run's predicted classes, e.g. { out: 12, compliant: 88 }. */
  pred_counts?: Record<string, number>;
}

/**
 * Response shape for GET /v1/eval/runs/{id}/pr-comp-metrics.
 * Mirrors backend `PrCompMetricsResponse`; we project this into `PrCompResult`
 * when patching it onto a run's `result.PR_COMP` for the Compare page.
 */
export interface PrCompMetricsResponse {
  run_id: string;
  solution_type: 'pc_v2' | 'legacy_match_oos';
  folded: PrCompPrecisionRecall;
  out_only: PrCompPrecisionRecall;
  faceover_only: PrCompPrecisionRecall;
  image_count: number;
  pred_counts?: Record<string, number>;
}

export interface RunResult {
  OD?: OdRunResult;
  PR?: PrRunResult;
  PC?: PcRunResult;
  PR_COMP?: PrCompResult;
}

export interface EvalModelIds {
  // Each value is a ModelRegistry mongo `_id` string (canonical identity).
  OD: string | null;
  VE: string | null;
  PC: string | null;
}

export interface EvalModelInfoEntry {
  model_name: string;
  version: string;
}

export interface EvalModelInfo {
  // Resolved display info per layer; null when the model_id is unset or unresolved.
  OD: EvalModelInfoEntry | null;
  VE: EvalModelInfoEntry | null;
  PC: EvalModelInfoEntry | null;
}

export interface EvalRun {
  run_id: string;
  run_type: RunType;
  status: RunStatus;
  model_ids: EvalModelIds;
  model_info: EvalModelInfo;
  dataset_id: string;
  dataset_name: string | null;
  source_run_id: string | null;
  result: RunResult | null;
  is_outdated: boolean | null;
  inferences_expired: boolean;
  triggered_by: string;
  triggered_at: string;
  started_at: string | null;
  completed_at: string | null;
  // detail-only fields
  config?: Record<string, unknown>;
  reason?: string | null;
  comment?: string | null;
  can_edit_comment?: boolean;
  failed_count?: number;
}

export function odMeanF1(od: OdRunResult | undefined): number | null {
  if (!od) return null;
  const vals = [od.cartons, od.products, od.shelves, od.mods, od.template_boxes, od.price_tags]
    .map((m) => m?.f1)
    .filter((v): v is number => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

export interface EvalRunInference {
  run_inference_id: string;
  run_id: string;
  gt_sample_id: string;
  pr_inference: Record<string, unknown> | null;
  pc_inference: Record<string, unknown> | null;
  metrics: RunResult | null;
  created_at: string;
  error?: string | null;
}

export interface RunInferenceDetail {
  run_inference_id: string;
  run_id: string;
  gt_sample: EvalGtSample;
  pr_inference: Record<string, unknown> | null;
  pc_inference: Record<string, unknown> | null;
  metrics: RunResult | null;
  // Maps class_name → { pr_shape_uuid → gt_shape_uuid } for TP/FP/FN classification.
  // Absent means the run does not carry assignment data.
  run_to_gt_assignment: Record<string, Record<string, string>> | null;
  error?: string | null;
}

export interface RunProgress {
  completed: number;
  total: number;
  failed?: number;
}

export interface RunInferencesListResponse {
  items: EvalRunInference[];
  total: number;
  run_status: RunStatus;
  progress: RunProgress;
}

export interface GtSampleRunEntry {
  run_id: string;
  run_type: RunType;
  status: RunStatus;
  model_ids: Record<string, string | null>;
  triggered_at: string;
  metrics: RunResult;
}

export interface GtSampleRunsResponse {
  gt_sample_id: string;
  total: number;
  items: GtSampleRunEntry[];
}

export interface MetricsRow {
  run_id: string;
  run_type: RunType;
  triggered_at: string;
  triggered_by: string;
  model_versions: {
    OD: string | null;
    VE: string | null;
    PC: string | null;
  };
  result: RunResult | null;
}

export interface MetricsOverviewResponse {
  dataset_id: string;
  dataset_name: string;
  total: number;
  rows: MetricsRow[];
}

export interface ModelsListResponse {
  items: EvalModel[];
  total: number;
}

export interface DatasetsListResponse {
  items: EvalGtDataset[];
  total: number;
}

export interface RunsListResponse {
  items: EvalRun[];
  total: number;
}

export interface GtSamplesListResponse {
  items: EvalGtSample[];
  total: number;
}

export interface PrInferenceListItem {
  inference_id: string;
  chain_id: number | null;
  store_id: number | null;
  camera_id: number | null;
  image_id: number | null;
  products_count: number;
  cartons_count: number;
  mods_count: number;
  price_tags_count: number;
  created_at: string;
}

export interface PrInferencesListResponse {
  items: PrInferenceListItem[];
  has_next: boolean;
}

export interface BulkIngestResponse {
  created: string[];
  skipped: string[];
}
